import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useMemo, useRef } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import { CssUiComponent } from '@webapp/common/components/survey-custom';
import { Textfield } from '@webapp/common/components/textfield';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';

import type { MatrixCell } from '../matrix-layout/matrix-cell';
import cssLayout from '../matrix-layout/matrix-layout.css';

import css from './matrix-select.css';

export const GroupSelect: VFC<MatrixCell> = ({
    answer,
    group: groupActor,
    height,
    question,
    disabled,
    onChange: changeCb,
    maxFilled,
}) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const group = useSelector(groupActor, (g) => g.context);
    const round = question.type !== QuestionType.MATRIX_FEW_ANSWERS;
    const { id, name, type } = group;
    const self = type === AnswerType.SELF;

    const responseActor = group.responses[answer.id];
    const response = useSelector(responseActor, (r) => r.context);
    const checked = Boolean(response?.value);

    const textRef = useRef(null);
    const value = String(response?.extra || '');

    const onChange = useCallback(
        (value: boolean) => {
            if (disabled) {
                changeCb?.();
            }
            groupActor.send({ type: 'CHANGE_RESPONSE_VALUE', value, answerId: answer.id });
        },
        [groupActor, disabled],
    );

    const onChangeExtra = useCallback(
        (value: string) => {
            if (disabled) {
                changeCb?.();
            }
            groupActor.send({ type: 'CHANGE_RESPONSE_EXTRA', value, answerId: answer.id });
            groupActor.send({ type: 'CHANGE_RESPONSE_VALUE', value: true, answerId: answer.id });
        },
        [answer.id, groupActor, disabled],
    );

    const { answerStyle, itemsGap } = useTextStyle();

    const checkboxStyle = useMemo(
        // TODO use checkbox styles
        () => ({
            border: `2px solid ${answerStyle.color}`,
            backgroundColor: checked ? answerStyle.color : undefined,
        }),
        [checked, answerStyle.color],
    );

    const fieldStyle = useMemo(() => ({ rowGap: itemsGap }), [itemsGap]);
    const cellStyle = useMemo(() => ({ height }), [height]);

    const onCheck = useCallback(
        (value: boolean) => {
            void onChange(value);
            const input = textRef.current;

            if (input && self && value) {
                input.focus();
            }
        },
        [answer, group, onChange, self],
    );

    const onClickSelf = useCallback(() => {
        void onChange(true);
    }, [answer, group, onChange]);

    return (
        <div className={cn(CssUiComponent.CELL, cssLayout.cell, css.answer)} key={id} style={cellStyle}>
            <div className={cn(css.field, self && css.self)} style={fieldStyle}>
                <Checkbox
                    checked={disabled ? false : checked}
                    label={pipingAnswersReplacer(name)}
                    labelClassName={css.label}
                    round={round}
                    style={checkboxStyle}
                    onChange={onCheck}
                    disabled={!checked && maxFilled}
                />
                {self && (
                    <Textfield
                        transparent
                        className={cssLayout.control}
                        ref={textRef}
                        rootClassName={css.text}
                        value={value}
                        onChange={onChangeExtra}
                        onClick={onClickSelf}
                    />
                )}
            </div>
        </div>
    );
};
GroupSelect.displayName = 'Group';
